import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import AssociativeAppShell from '../../components/AssociativeAppShell';
import SupportBackLink from '../../components/support/SupportBackLink';
import ChannelSettingsPanel, { FORBIDDEN_CHANNELS_TEXT } from '../../components/support/ChannelSettingsPanel';
import { representativeSupportApi } from '../../services/adapters/representativeSupportApi';
import { getMyProfile, removeRepresentativeToken } from '../../services/representativeService';

interface RepProfile {
  _id: string;
  nome: string;
  cognome: string;
  tipo: 'interno' | 'esterno';
  caricaPrincipale?: string;
  foto?: string;
  associatesAccess?: boolean;
  permissions: { name: string; description: string }[];
}

export const MANAGE_CHANNEL_PERMISSION = 'associativa.supporto.manage_channel';

// Impostazioni dei canali del responsabile di canale (Task 46). Guardia: il
// permesso `associativa.supporto.manage_channel` nel profilo; il backend resta
// l'arbitro (un 403 di `channels/manage` mostra lo stesso avviso).
const AssociativeSupportCanali: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [profile, setProfile] = useState<RepProfile | null>(null);
  const canLive = profile?.permissions?.some((p) => p.name === 'associativa.live.publish') ?? false;
  const canManage = profile?.permissions?.some((p) => p.name === MANAGE_CHANNEL_PERMISSION) ?? false;

  useEffect(() => {
    const token = localStorage.getItem('representative_token');
    if (!token) {
      navigate('/accesso-associati');
      return;
    }
    const loadProfile = async () => {
      try {
        const res = await getMyProfile();
        if (res.success) setProfile(res.data);
      } catch (err: any) {
        if (err.response?.status === 401 || err.response?.status === 403) {
          removeRepresentativeToken();
          navigate('/accesso-associati');
          return;
        }
        toast.error('Errore caricamento profilo');
      }
    };
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return (
    <AssociativeAppShell
      route="supporto"
      nome={profile?.nome}
      cognome={profile?.cognome}
      caricaPrincipale={profile?.caricaPrincipale}
      tipo={profile?.tipo}
      foto={profile?.foto}
      associatesAccess={profile?.associatesAccess}
      canLive={canLive}
    >
      <div className="page fade-in">
        <SupportBackLink to="/area-associativa/supporto" label="Torna al supporto" />
        {!profile ? null : canManage ? (
          <ChannelSettingsPanel api={representativeSupportApi} selfId={profile._id} />
        ) : (
          <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-5 max-w-xl" role="alert">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">Funzione non abilitata per la tua carica</p>
            <p className="text-sm text-amber-900/80 dark:text-amber-200/80 mt-1">{FORBIDDEN_CHANNELS_TEXT}</p>
          </div>
        )}
      </div>
    </AssociativeAppShell>
  );
};

export default AssociativeSupportCanali;
